import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import CircularProgress from '@mui/material/CircularProgress';

export default function RegistrationForm({ onSubmit, loading }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'user',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f8ff, #e6f7ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 600, md: 700 },
          borderRadius: 4,
          boxShadow: '0 20px 40px -10px rgba(0, 0, 100, 0.2)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
          overflow: 'visible',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 25px 50px -10px rgba(0, 0, 100, 0.25)',
          },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          background: 'linear-gradient(to bottom right, rgba(255,255,255,0.9), rgba(255,255,255,1))',
          backdropFilter: 'blur(8px)',
        }}
      >
        <CardContent sx={{ p: isMobile ? 3 : 5 }}>
          <Box 
            textAlign="center" 
            mb={4}
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '3px',
                background: 'linear-gradient(to right, #1976d2, #64b5f6)',
                borderRadius: 3,
              }
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 2,
                boxShadow: '0 8px 16px rgba(25, 118, 210, 0.2)',
                border: '3px solid white',
              }}
            >
              <PersonAddAltIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Create User
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Register a new user and assign them a role
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3.5}>
              <TextField
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    fontSize: '16px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.15)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }
                }}
                InputLabelProps={{
                  sx: {
                    color: 'text.secondary',
                  }
                }}
              />
              
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    fontSize: '16px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.15)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }
                }}
                InputLabelProps={{
                  sx: {
                    color: 'text.secondary',
                  }
                }}
              />
              
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'text.secondary' }}>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  label="Role"
                  sx={{
                    borderRadius: 2,
                    '& .MuiSelect-select': {
                      fontSize: '16px',
                    },
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.15)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </Select>
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 2,
                  py: 1.8,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    background: loading ? '' : 'primary.dark',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'common.white' }} />
                ) : (
                  'Register User'
                )}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}